import { useEffect, useRef, useState } from 'react';

import { geocodeAddressApi } from '@api-client';
import type { AddressSuggestion } from '@shared/types/entities';

interface UseAddressSearchOptions {
  debounceMs?: number;
  minLength?: number;
}

interface UseAddressSearchResult {
  suggestions: AddressSuggestion[];
  isLoading: boolean;
  search: (q: string) => void;
  clear: () => void;
}

export function useAddressSearch(options: UseAddressSearchOptions = {}): UseAddressSearchResult {
  const { debounceMs = 300, minLength = 3 } = options;
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);

  const search = (q: string) => {
    clearTimeout(timerRef.current);

    if (!q || q.trim().length < minLength) {
      setSuggestions([]);
      return;
    }

    timerRef.current = setTimeout(() => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setIsLoading(true);

      geocodeAddressApi(q.trim())
        .then(setSuggestions)
        .catch(() => setSuggestions([]))
        .finally(() => setIsLoading(false));
    }, debounceMs);
  };

  const clear = () => {
    clearTimeout(timerRef.current);
    setSuggestions([]);
    setIsLoading(false);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return { suggestions, isLoading, search, clear };
}
